import React from "react";
import {BadgeCheckIcon, Calendar, ClockIcon, DocumentIcon, LockIcon, PaperPlaneIcon} from "assets/icons";
import Button from "components/button";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import ModalFooter from "components/modal/components/footer";
import useModal from "components/modal/useModal";
import useLicenceTexts from "components/licences/useLicenceTexts";
import UseContext from "hooks/useContext";
import {formatInstantDate} from "helper/licences";
import {getInterimPdfUrl} from "helper/interimPdfUrl";
import {formatCalendarDate} from "helper/residencePermit";

// gray-900 and yellow-500 of tailwind.config.js
const INFO_ICON_COLOR = "#111827";
const ACCEPTED_ICON_COLOR = "#FF9600";

const InfoRow = ({Icon, iconSize = 20, title, text}) => (
    <li className="flex gap-3">
        <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg border border-gray-200">
            <Icon wh={iconSize} color={INFO_ICON_COLOR}/>
        </div>
        <div className="flex flex-col">
            <span className="ft-sm font-medium text-gray-900">{title}</span>
            <span className="ft-sm text-gray-500">{text}</span>
        </div>
    </li>
);

const LicenceDetailModal = () => {
    const {objID, tempData, setModalType, setLicenceModalPreset} = UseContext();
    const {toggle} = useModal();
    const {translate, getLabel, getTypeWord} = useLicenceTexts();
    const entry = (Array.isArray(tempData?.licences) ? tempData.licences : []).find((licence) => licence?._id === objID);
    const closeModal = () => toggle();

    if (!entry) {
        return <Modalheader hide={closeModal} hideShow={true}/>;
    }

    const type = getTypeWord(entry);
    const t = (key, values) => translate(`detail_modal.${key}`, {type, ...values});

    // Switches the open modal to the add flow (j_c_licence), which reads the preset when it mounts.
    const openAddFlow = (replacedLicenceId) => {
        setLicenceModalPreset({licence: entry.licence, category: entry.category || null, replacedLicenceId});
        setModalType("j_c_licence");
    };

    const openDocument = () => {
        window.open(getInterimPdfUrl({type: "licence", licenceId: entry._id}), "_blank", "noopener,noreferrer");
    };

    const closeButton = (
        <Button size={"LG"} color={"SECONDARY"} onClick={closeModal}>
            {t("close")}
        </Button>
    );

    if (entry.state === "pending") {
        return (
            <>
                <Modalheader hide={closeModal} hideShow={true}>
                    <span className="flex flex-col gap-4">
                        <PaperPlaneIcon wh={28} color={INFO_ICON_COLOR}/>
                        {t("pending_title")}
                    </span>
                </Modalheader>
                <ModalBody>
                    <ul className="flex flex-col gap-4">
                        <InfoRow Icon={ClockIcon} title={t("delay_title")} text={t("delay_text")}/>
                        <InfoRow Icon={Calendar} title={t("end_date_title")} text={t("end_date_text")}/>
                    </ul>
                </ModalBody>
                <ModalFooter>
                    {closeButton}
                    <Button size={"LG"} color={"PRIMARY"} onClick={openDocument}>
                        {t("view_document")}
                    </Button>
                </ModalFooter>
            </>
        );
    }

    if (entry.state === "refused") {
        return (
            <>
                <Modalheader hide={closeModal} hideShow={true}>
                    {t("refused_title")}
                </Modalheader>
                <ModalBody>
                    {entry.reason && (
                        <div className="flex flex-col gap-1 rounded-lg bg-red-50 px-4 py-3 text-red-700">
                            <span className="ft-sm font-semibold">{t("refusal_reason_title")}</span>
                            <ul className="ft-sm list-disc pl-5">
                                <li>{entry.reason}</li>
                            </ul>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    {closeButton}
                    <Button size={"LG"} color={"PRIMARY"} onClick={() => openAddFlow(entry._id)}>
                        {t("import_new")}
                    </Button>
                </ModalFooter>
            </>
        );
    }

    // valid, expiring, expired, accepted: verified entries, replaced by a new upload once it is checked.
    const expirationDate = formatCalendarDate(entry.expiration);
    const validatedDate = formatInstantDate(entry.validatedAt);
    const stateText = t(`state_text.${entry.state}`) || t("state_text.accepted");

    return (
        <>
            <Modalheader hide={closeModal} hideShow={true}>
                <span className="flex flex-col gap-4">
                    <BadgeCheckIcon wh={40} color={ACCEPTED_ICON_COLOR}/>
                    {getLabel(entry)}
                </span>
            </Modalheader>
            <ModalBody>
                <div className="flex flex-col gap-4">
                    <p className="ft-sm text-gray-500">{stateText}</p>
                    <ul className="flex flex-col gap-4">
                        {expirationDate && (
                            <InfoRow
                                Icon={Calendar}
                                title={t("valid_until_title")}
                                text={t("valid_until_text", {date: expirationDate})}
                            />
                        )}
                        {validatedDate && (
                            <InfoRow
                                Icon={DocumentIcon}
                                iconSize={36}
                                title={t("document_title")}
                                text={t("document_verified_on", {date: validatedDate})}
                            />
                        )}
                        <InfoRow Icon={LockIcon} title={t("not_deletable_title")} text={t("not_deletable_text")}/>
                    </ul>
                </div>
            </ModalBody>
            <ModalFooter>
                {closeButton}
                <Button size={"LG"} color={"PRIMARY"} onClick={() => openAddFlow(null)}>
                    {t("add_new")}
                </Button>
            </ModalFooter>
        </>
    );
};

export default LicenceDetailModal;
